<template>
  <LMap
    :zoom.sync="zoom"
    :center="center"
    :options="{ zoomControl: false, attributionControl: false }"
    style="height: 100%; width: 100%; z-index: 0"
    ref="map"
    @moveend="updateCenter"
    @ready="mapReady"
  >
    <LTileLayer
      v-for="tile in tileProviders"
      :key="tile.name"
      :name="tile.name"
      :visible="tile.visible"
      :url="tile.url"
      :attribution="tile.attribution"
      layer-type="base"
    />

    <LControlLayers position="bottomleft" />

    <template v-if="layersState.Nodes && nodes.length">
      <LLayerGroup name="Nodes" @ready="exportNodesPosition">
        <LMarker
          v-for="(node, i) in nodes"
          :key="node.id"
          :lat-lng="node.coordinates"
          :draggable="dragMode"
          :z-index-offset="1000"
          @update:lat-lng="(coords) => updateNode(coords, i)"
          @ready="(element) => nodeReady(element, node.id)"
          :icon="circleIcon(node.icon || 'default', '#306D8A', borderColor(node))"
          :ref="`node-${node.id}`"
        >
          <LTooltip
            :options="{
              permanent: true,
              interactive: true,
              direction: 'top',
              offset: point(0, -10),
              className: 'leaflet-tooltip-custom-style',
            }"
            ><span :class="toggleTooltipClass">
              {{ node.name }}
            </span>
          </LTooltip>
        </LMarker>
      </LLayerGroup>
    </template>
    <LMarker
      v-if="nodeMode"
      key="newNode"
      :lat-lng.sync="newNode"
      :icon="circleIcon('default', 'black')"
      ref="`newNode`"
    >
    </LMarker>
    <template v-if="layersState.Branches && branches.length">
      <LLayerGroup name="Branches">
        <LPolyline
          :lat-lngs="branchPoints[i]"
          @ready="(element) => branchReady(element, branch.id)"
          :weight="deleteMode ? 6 : 3"
          :color="voltageToColor(branch.voltage)"
          bubbling-mouse-events
          v-for="(branch, i) in branches"
          :key="`branch-${branch.id}`"
        >
        </LPolyline>
      </LLayerGroup>
      <LLayerGroup name="BranchesCoordinates" v-if="editPolylineMode">
        <template v-for="(branch, i) in branches">
          <LMarker
            v-for="(sub, j) in branch.coordinates"
            :key="`branch-${i}-subranch-${j}`"
            :lat-lng="sub"
            draggable
            :icon="svgShape('circle', 'black', 'black')"
            @update:lat-lng="(point) => updateSB(point, i, j)"
            @ready="(element) => nodeMarkerReady(element, i, j)"
            :z-index-offset="500"
          ></LMarker>
          <LMarker
            v-for="(mid, k) in midPoints(branchPoints[i])"
            :key="`branch-${i}-newsubranch-${k}`"
            :lat-lng="mid"
            :icon="svgShape('circle', 'gray', 'gray')"
            @ready="(event) => markerReady(event, i, k)"
          ></LMarker>
        </template>
      </LLayerGroup>
    </template>

    <template v-if="layersState.Resources && generators.length">
      <LLayerGroup name="Generators">
        <LMarker
          v-for="generator in generators"
          :key="generator.id"
          :lat-lng="generator.coordinates"
          :icon="circleIcon(generator.icon, '#306D8A')"
          :ref="`generator-${generator.id}`"
          ><LPopup>
            <div>
              {{ `${generator.id} - ${generator.name}` }}
            </div>
          </LPopup>
        </LMarker>
      </LLayerGroup>
    </template>

    <template v-if="layersState.Resources && storages.length">
      <LLayerGroup name="storages">
        <LMarker
          v-for="storage in storages"
          :key="storage.id"
          :lat-lng="storage.coordinates"
          :icon="customIcon('storage')"
          :ref="`storage-${storage.id}`"
          ><LPopup>
            <div>
              {{ `${storage.id} - ${storage.name}` }}
            </div>
          </LPopup>
        </LMarker>
      </LLayerGroup>
    </template>

    <template v-if="layersState.Resources && layersState.Nodes && resourcesBranches.length">
      <LLayerGroup name="Resources Connections">
        <LPolyline
          v-for="(branch, i) in resourcesBranches"
          :key="i"
          :lat-lngs="branch.latLngs"
          :weight="2"
          color="#2962FF"
        ></LPolyline>
      </LLayerGroup>
    </template>

    <LControl position="topleft">
      <v-btn-toggle v-model="mapMode" tile class="button-direction button-location">
        <v-tooltip right>
          <template #activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" value="node">
              <v-icon>mdi-map-marker-plus</v-icon>
            </v-btn>
          </template>
          <span>Add Node</span>
        </v-tooltip>
        <v-tooltip right>
          <template #activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" value="branch">
              <v-icon>mdi-transmission-tower</v-icon>
            </v-btn>
          </template>
          <span>Add Transmission Line</span>
        </v-tooltip>
        <!-- <v-btn value="generator">
          <v-icon>mdi-wind-turbine</v-icon>
        </v-btn> -->
        <!-- <v-btn value="project">
          <v-icon>mdi-account-hard-hat</v-icon>
        </v-btn> -->
        <!-- <v-btn value="transformer">
          <v-icon>mdi-vector-polyline-edit</v-icon>
        </v-btn>
        <v-btn value="project">
          <v-icon>mdi-vector-polyline-edit</v-icon>
        </v-btn> -->
      </v-btn-toggle>
    </LControl>

    <LControl position="topleft">
      <v-btn-toggle v-model="mapMode" tile class="button-direction">
        <v-tooltip right>
          <template #activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" value="drag">
              <v-icon>mdi-cursor-move</v-icon>
            </v-btn>
          </template>
          <span>Drag elements</span>
        </v-tooltip>
        <v-tooltip right>
          <template #activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" value="edit-polyline">
              <v-icon>mdi-vector-polyline-edit</v-icon>
            </v-btn>
          </template>
          <span>Edit Transmission Line</span>
        </v-tooltip>
        <v-tooltip right>
          <template #activator="{ on, attrs }">
            <v-btn v-on="on" v-bind="attrs" value="delete">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
          <span>Delete elements</span>
        </v-tooltip>
      </v-btn-toggle>
    </LControl>

    <LControl position="bottomright" class="control-layers">
      <v-card width="100" outlined elevation="0" style="margin-bottom: 100px">
        <v-row class="no-gutters">
          <v-card class="grow" flat>
            <p class="my-0 text-caption font-weight-medium text-center">Voltage (kV)</p>
            <!-- <p class="my-0 pl-1 text-caption font-weight-medium text-center">hi</p> -->
          </v-card>
        </v-row>
        <v-row v-for="rule in branchColorRules" :key="rule.color" class="no-gutters">
          <v-card class="d-flex grow pa-1" flat>
            <v-card :color="rule.color" height="16" width="16" flat></v-card>
            <v-spacer />
            <p class="mb-0 ml-1">[{{ rule.from }}, {{ rule.to }})</p>
          </v-card>
        </v-row>
        <v-row key="#6E6E6E" class="no-gutters">
          <v-card class="d-flex grow pa-1" flat>
            <v-card :color="'#6E6E6E'" height="16" width="16" flat></v-card>
            <v-spacer />
            <p class="mb-0 ml-1">Not defined</p>
          </v-card>
        </v-row>
      </v-card>
    </LControl>
  </LMap>
</template>

<script>
import {
  LControl,
  LControlLayers,
  LLayerGroup,
  LMarker,
  LMap,
  LPolyline,
  LPopup,
  LTileLayer,
  LTooltip,
} from "vue2-leaflet";
import { latLngBounds, Point } from "leaflet";
import { svgShape, circleIcon, customIcon, generatorIconName } from "@/plugins/map-utils";
import { CaseModel } from "data-dictionary";

export default {
  // TODO: Change to a representative name
  name: "GeographicalDiagram",
  components: {
    LControl,
    LControlLayers,
    LLayerGroup,
    LMarker,
    LTooltip,
    LMap,
    LPolyline,
    LPopup,
    LTileLayer,
  },
  mixins: [],
  props: {
    currentCase: { type: Object, required: false, default: () => ({}) },
    useNodesCoordinates: { type: Boolean, required: false, default: false },
    mapCenter: { type: Object, required: false, default: undefined },
  },
  data() {
    return {
      zoom: 5,
      center: [40.71, -74.006],
      tileProviders: [
        {
          name: "Map",
          visible: true,
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        },
        {
          name: "Satellite",
          visible: false,
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          attribution:
            "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
        },
      ],
      layersState: {
        Nodes: true,
        Resources: true,
        Branches: true,
        Tooltip: false,
      },
      mapMode: "default",
      branchState: 0,
      branchSelectedNode1: null,
      newNode: [0, 0],

      branchColorRules: [
        { from: 0, to: 1, color: "#000500" },
        { from: 1, to: 4, color: "#ff7f0e" },
        { from: 4, to: 7.5, color: "#2ca02c" },
        { from: 7.5, to: 11, color: "#d62728" },
        { from: 11, to: 12.5, color: "#9467bd" },
        { from: 12.5, to: 21, color: "#8c564b" },
        { from: 21, to: 34.5, color: "#e377c2" },
        { from: 34.5, to: 110, color: "#11a579" },
        { from: 110, to: 154, color: "#bcbd22" },
        { from: 154, to: 220, color: "#17becf" },
        { from: 220, to: 345, color: "#1f77b4" },
        { from: 345, to: 500, color: "#e6ac18" },
      ],
    };
  },
  computed: {
    /**
     * Map Modes
     */

    // Does nothing, default state
    defaultMode() {
      return this.mapMode === "default";
    },

    // Adding a new node
    nodeMode() {
      return this.mapMode === "node";
    },

    // Adding a new branch between two nodes
    branchMode() {
      return this.mapMode === "branch";
    },

    // Moving nodes arround
    dragMode() {
      return this.mapMode === "drag";
    },

    // Editing branches coordinates
    editPolylineMode() {
      return this.mapMode === "edit-polyline";
    },

    // Deleting branches and nodes
    deleteMode() {
      return this.mapMode === "delete";
    },

    computedCase: {
      get() {
        return this.currentCase;
      },
      set(val) {
        this.$emit("update:current-case", val);
        this.$emit("change");
      },
    },
    branchPoints() {
      return this.branches.map((branch) => {
        const fromNode = this.nodes.find((n) => n.id === branch.node_from);
        const toNode = this.nodes.find((n) => n.id === branch.node_to);
        const branchCoordinates = branch.coordinates ? branch.coordinates : [];
        const fromNodeCoordinates = fromNode ? [fromNode.coordinates] : [];
        const toNodeCoordinates = toNode ? [toNode.coordinates] : [];

        const coordinates = [...fromNodeCoordinates, ...branchCoordinates, ...toNodeCoordinates];
        return coordinates.every((v) => v) ? coordinates : [];
      });
    },
    nodes() {
      if (this.computedCase && this.computedCase.input && this.computedCase.input.nodes) {
        return this.computedCase.input.nodes;
      }
      return [];
    },
    branches() {
      if (this.computedCase && this.computedCase.input && this.computedCase.input.branches) {
        return this.computedCase.input.branches;
      }
      return [];
    },
    generators() {
      if (this.computedCase && this.computedCase.input && this.computedCase.input.generators) {
        return this.computedCase.input.generators
          .filter((v) => v.coordinates && v.coordinates.every((u) => u))
          .map((v) => ({ ...v, icon: generatorIconName(v) }));
      }
      return [];
    },
    storages() {
      if (this.computedCase && this.computedCase.input && this.computedCase.input.generators) {
        return this.computedCase.input.storage
          .filter((v) => v.coordinates && v.coordinates.every((u) => u))
          .map((v) => ({ ...v, icon: "storage" }));
      }
      return [];
    },
    resourcesBranches() {
      const branches = [];
      ["generators", "storages"].forEach((resourceType) => {
        branches.push(
          ...this[resourceType]
            .filter((v) => v.bus_id)
            .map((v) => ({
              latLngs: [v.coordinates, this.nodes.find((node) => node.id === v.bus_id).coordinates],
            }))
        );
      });

      return branches;
    },
    mapBounds() {
      if (!this.nodes.length) {
        return null;
      }
      const bounds = latLngBounds([...this.nodes.map((n) => n.coordinates)]);
      return Object.keys(bounds).length ? bounds.pad(0.3) : bounds;
    },
    newMarker: {
      get() {
        return this.item ? this.item.coordinates : null;
      },
      set(v) {
        const itemCopy = this.item;
        itemCopy.coordinates = Object.values(v);
        this.$emit("update:item", itemCopy);
      },
    },
    showTooltip() {
      // Hardcoded. Desired to show marker's tooltip based on zoom level
      return false;
    },
    // CSS trick to make tooltips disappear
    toggleTooltipClass() {
      return {
        "tooltip-invisible-text": !this.layersState.Tooltip,
        "tooltip-text": this.layersState.Tooltip,
      };
    },
  },
  watch: {
    mapMode(value) {
      if (value !== "branch") {
        this.branchSelectedNode1 = null;
      }
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    customIcon,
    svgShape,
    circleIcon,
    branchReady(polyline, branchId) {
      polyline.on("click", () => {
        if (this.deleteMode) {
          this.branches.splice(
            this.branches.findIndex((branch) => branch.id === branchId),
            1
          );
          this.$emit("change");
        }
      });
    },
    nodeReady(marker, nodeId) {
      marker.on("click", () => {
        if (this.deleteMode) {
          this.nodes.splice(
            this.nodes.findIndex((node) => node.id === nodeId),
            1
          );
          this.$emit("change");
        }
        if (this.branchMode) {
          if (!this.branchSelectedNode1) {
            this.branchSelectedNode1 = nodeId;
          } else if (this.branchSelectedNode1 === nodeId) {
            this.branchSelectedNode1 = null;
          } else {
            this.addBranch(this.branchSelectedNode1, nodeId);
            this.branchSelectedNode1 = null;
          }
        }
      });
    },
    markerReady(marker, branch, insert) {
      marker.on("click", (event) => this.createSB(event, branch, insert));
    },
    nodeMarkerReady(marker, branch, subranch) {
      marker.on("contextmenu", (event) => this.deleteSB(event, branch, subranch));
    },
    createSB(event, branch, insert) {
      if (!this.branches[branch].coordinates) {
        this.$set(this.branches[branch], "coordinates", []);
      }
      this.branches[branch].coordinates.splice(insert, 0, this.eventToCoordinates(event));
    },
    updateNode(coords, id) {
      this.$set(this.nodes[id], "coordinates", [coords.lat.toFixed(3), coords.lng.toFixed(3)]);
      this.$emit("change");
    },
    updateSB(coords, branch, subranch) {
      this.branches[branch].coordinates[subranch] = [coords.lat.toFixed(3), coords.lng.toFixed(3)];
      this.branches.splice(branch, 1, this.branches[branch]);
      this.$emit("change");
    },
    // Work In Progress
    // updateGenerator(coords, id) {
    //   this.generators[id].coordinates = [coords.lat.toFixed(3), coords.lng.toFixed(3)];
    //   this.generators.splice(id, 1, this.generators[id]);
    //   this.$emit("change");
    // },
    deleteSB(event, branch, subranch) {
      this.branches[branch].coordinates.splice(subranch, 1);
      this.$emit("change");
    },
    middlePoint(node1, node2) {
      const [lat1, lng1] = node1;
      const [lat2, lng2] = node2;

      const latM = (parseFloat(lat1) + parseFloat(lat2)) / 2;
      const lngM = (parseFloat(lng1) + parseFloat(lng2)) / 2;

      return [latM.toString(), lngM.toString()];
    },
    midPoints(points) {
      const midpoints = [];
      for (let i = 1; i < points.length; i += 1) {
        midpoints.push(this.middlePoint(points[i], points[i - 1]));
      }
      return midpoints;
    },
    exportNodesPosition() {
      if (this.useNodesCoordinates) {
        const positions = [];
        this.nodes.forEach((node) => {
          const pos = this.$refs.map.mapObject.latLngToContainerPoint(
            this.$refs[`node-${node.id}`][0].latLng
          );
          positions.push({ id: node.id, ...pos });
        });
        return this.$emit("update:node-positions", positions);
      }
      return null;
    },
    updateCenter() {
      this.$emit("update:map-center", this.$refs.map.mapObject.getCenter());
    },
    mapReady(map) {
      this.centerMap();
      map.on("mousemove", (event) => {
        this.newNode = event.latlng;
      });
      map.on("click", (event) => {
        if (this.nodeMode) this.addNode(event);
      });
    },
    centerMap() {
      this.$refs.map.mapObject.fitBounds(this.mapBounds);
      this.$emit("update:map-center", this.$refs.map.mapObject.getCenter());
    },
    point(x, y) {
      return new Point(x, y);
    },
    getNextId(items, offset = 0) {
      if (!items.length) return 1;
      return Math.max(...items.map((e) => e.id)) + 1 + offset;
    },
    addBranch(nodeFrom, nodeTo) {
      const obj = new CaseModel.Branch();
      obj.id = this.getNextId(this.branches);
      obj.name = `${obj.name} ${obj.id}`;
      obj.node_from = nodeFrom;
      obj.node_to = nodeTo;
      obj.coordinates = [];
      this.branches.splice(this.branches.length, 0, obj);
      this.$emit("change");
    },
    addNode(event) {
      const obj = new CaseModel.Node({});
      obj.id = this.getNextId(this.nodes);
      obj.name = `${obj.name} ${obj.id}`;
      obj.coordinates = this.eventToCoordinates(event);
      obj.icon = "default";
      this.nodes.splice(this.nodes.length, 0, { ...obj });
      this.$emit("change");
    },
    eventToCoordinates(event) {
      return [event.latlng.lat.toString(), event.latlng.lng.toString()];
    },
    borderColor(node) {
      if (this.branchSelectedNode1 === node.id) {
        return "#000000";
      }
      return "#90D7EC";
    },
    voltageToColor(voltage) {
      if (voltage === null || voltage === 0 || voltage === "") {
        return "#6E6E6E";
      }
      const colorRule = this.branchColorRules.find(
        (rule) => rule.from <= voltage && voltage < rule.to
      );

      return colorRule
        ? colorRule.color
        : this.branchColorRules[this.branchColorRules.length - 1].color;
    },
  },
};
</script>

<style>
.control-layers {
  right: 10px !important;
}
.dense-checkbox .v-label {
  font-size: 14px !important;
}
.dense-checkbox .v-input--selection-controls__input {
  margin-right: 4px;
}
.button-direction {
  flex-direction: column;
  margin-left: 2px;
}
.button-location {
  margin-top: 100px;
}
.leaflet-tooltip-custom-style {
  background-color: rgba(0, 0, 0, 0);
  border-color: rgba(0, 0, 0, 0);
  box-shadow: none;
}
.leaflet-tooltip-custom-style:before {
  background-color: rgba(0, 0, 0, 0);
  border-color: rgba(0, 0, 0, 0);
  box-shadow: none;
}
.tooltip-text {
  color: black;
  background-color: white;
  border-radius: 5px;
  padding: 2px;
}
.tooltip-invisible-text {
  background-color: transparent;
  color: transparent;
}
</style>
